import { useEffect, useMemo, useState } from "react";

function selectionKey(item) {
  return `${item.contentType}\u0000${item.contentId}`;
}

function statusKey(item) {
  return `${item.contentType}:${item.contentId}`;
}

function typeLabel(contentType) {
  if (contentType === "frameBinding") return "Frame activation";
  return `${String(contentType || "content").charAt(0).toUpperCase()}${String(contentType || "content").slice(1)}`;
}

export function useContentSync({ apiBase, token, items }) {
  const [capabilities, setCapabilities] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [integrityOk, setIntegrityOk] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [reviewPlan, setReviewPlan] = useState(null);
  const [notice, setNotice] = useState({ type: "", text: "" });
  const itemSignature = useMemo(
    () => (items || []).map((item) => selectionKey(item)).sort().join("|"),
    [items]
  );
  const itemMap = useMemo(
    () => new Map((items || []).map((item) => [selectionKey(item), item])),
    [items]
  );

  useEffect(() => {
    let active = true;
    async function loadCapabilities() {
      if (!token) return;
      try {
        const response = await fetch(`${apiBase}/api/admin/content-sync/capabilities`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "Failed to load content sync settings");
        if (active) setCapabilities(payload);
      } catch (err) {
        if (active) setCapabilities({ canAuthorContent: true, canPromoteContent: false });
      }
    }
    loadCapabilities();
    return () => {
      active = false;
    };
  }, [apiBase, token]);

  async function refreshStatus() {
    if (!capabilities?.canPromoteContent || !items?.length) return;
    setStatusLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/content-sync/status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ items: items.map(({ contentType, contentId }) => ({ contentType, contentId })) })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Failed to compare production content");
      setStatuses(payload.items || {});
      setIntegrityOk(payload.integrityOk !== false);
      setNotice({ type: "", text: "" });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to compare production content" });
    } finally {
      setStatusLoading(false);
    }
  }

  useEffect(() => {
    refreshStatus();
    // itemSignature deliberately represents the loaded table rows without depending on array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, token, capabilities?.canPromoteContent, itemSignature]);

  useEffect(() => {
    setSelectedKeys((previous) => {
      const next = new Set([...previous].filter((key) => itemMap.has(key)));
      return next.size === previous.size ? previous : next;
    });
  }, [itemMap]);

  function statusFor(item) {
    return statuses[statusKey(item)] || null;
  }

  function isSelected(item) {
    return selectedKeys.has(selectionKey(item));
  }

  function toggle(item) {
    const status = statusFor(item);
    if (!integrityOk || !status?.selectable) return;
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      const key = selectionKey(item);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function selectChanged(candidateItems) {
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      (candidateItems || []).forEach((item) => {
        if (statusFor(item)?.selectable && integrityOk) next.add(selectionKey(item));
      });
      return next;
    });
  }

  function clearSelection() {
    setSelectedKeys(new Set());
  }

  function selectedItems() {
    return [...selectedKeys].map((key) => itemMap.get(key)).filter(Boolean);
  }

  async function review() {
    const selected = selectedItems();
    if (!selected.length) return;
    setReviewLoading(true);
    setNotice({ type: "", text: "" });
    try {
      const response = await fetch(`${apiBase}/api/admin/content-sync/plan`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ items: selected.map(({ contentType, contentId }) => ({ contentType, contentId })) })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Failed to review selected content");
      setReviewPlan(payload.plan || null);
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to review selected content" });
    } finally {
      setReviewLoading(false);
    }
  }

  async function apply() {
    if (!reviewPlan?.planHash) return;
    const selected = selectedItems();
    setApplyLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/content-sync/apply`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          items: selected.map(({ contentType, contentId }) => ({ contentType, contentId })),
          planHash: reviewPlan.planHash
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Content promotion failed");
      setReviewPlan(null);
      clearSelection();
      setNotice({
        type: "success",
        text: `${payload.summary?.selected || selected.length} selected item${selected.length === 1 ? "" : "s"} synced to production.`
      });
      await refreshStatus();
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Content promotion failed" });
      setReviewPlan(null);
    } finally {
      setApplyLoading(false);
    }
  }

  return {
    apply,
    applyLoading,
    canAuthorContent: capabilities?.canAuthorContent !== false,
    clearSelection,
    integrityOk,
    isSelected,
    notice,
    refreshStatus,
    review,
    reviewLoading,
    reviewPlan,
    selectChanged,
    selectedCount: selectedKeys.size,
    setReviewPlan,
    showSync: capabilities?.canPromoteContent === true,
    statusFor,
    statusLoading,
    toggle
  };
}

export function ContentSyncCheckbox({ sync, item, label }) {
  if (!sync.showSync) return null;
  const status = sync.statusFor(item);
  const disabled = !sync.integrityOk || !status?.selectable;
  return (
    <input
      type="checkbox"
      checked={sync.isSelected(item)}
      disabled={disabled}
      aria-label={label}
      title={status?.blockedReason || (status?.state === "live" ? "Already live" : "Select for production sync")}
      onClick={(event) => event.stopPropagation()}
      onChange={() => sync.toggle(item)}
    />
  );
}

export function ContentSyncState({ sync, item }) {
  if (!sync.showSync) return null;
  const status = sync.statusFor(item);
  if (sync.statusLoading && !status) return <span className="content-sync-state checking">Checking...</span>;
  if (!status?.selectable && status?.state !== "live") {
    return <span className="content-sync-state unavailable" title={status?.blockedReason}>-</span>;
  }
  return (
    <span className={`content-sync-state ${status?.state === "live" ? "live" : "ready"}`}>
      {status?.state === "live" ? "Live" : "Changes ready"}
    </span>
  );
}

export function ContentSyncToolbar({ sync, visibleItems }) {
  if (!sync.showSync) return null;
  return (
    <>
      <div className="content-sync-toolbar">
        <div>
          <strong>Production sync</strong>
          <span>{sync.selectedCount ? `${sync.selectedCount} selected` : "Select individual rows to sync"}</span>
        </div>
        <div className="content-sync-toolbar-actions">
          <button
            className="secondary-button"
            type="button"
            disabled={!sync.integrityOk || sync.statusLoading}
            onClick={() => sync.selectChanged(visibleItems)}
          >
            Select changes in view
          </button>
          {sync.selectedCount > 0 && (
            <button className="secondary-button" type="button" onClick={sync.clearSelection}>
              Clear selection
            </button>
          )}
          <button
            className="primary-button"
            type="button"
            disabled={!sync.integrityOk || !sync.selectedCount || sync.reviewLoading}
            onClick={sync.review}
          >
            {sync.reviewLoading ? "Preparing..." : `Review and Sync (${sync.selectedCount})`}
          </button>
        </div>
      </div>

      {!sync.integrityOk && (
        <div className="admin-notice error">
          Production integrity check failed. Sync is blocked until production content is reconciled.
        </div>
      )}
      {sync.notice.text && (
        <div className={`admin-notice ${sync.notice.type === "error" ? "error" : "success"}`}>
          {sync.notice.text}
        </div>
      )}

      {sync.reviewPlan && (
        <div className="content-sync-modal-backdrop" role="presentation">
          <section className="content-sync-modal" role="dialog" aria-modal="true" aria-labelledby="content-sync-title">
            <div className="content-sync-modal-header">
              <div>
                <div className="content-sync-eyebrow">Production release</div>
                <h2 id="content-sync-title">Review selected content</h2>
                <p>
                  {sync.reviewPlan.summary?.selected || 0} selected
                  {sync.reviewPlan.summary?.required
                    ? ` + ${sync.reviewPlan.summary.required} required dependencies`
                    : " with no additional dependencies"}
                </p>
              </div>
              <button className="secondary-button" type="button" onClick={() => sync.setReviewPlan(null)}>
                Close
              </button>
            </div>

            <div className="content-sync-review-section">
              <h3>Selected</h3>
              {sync.reviewPlan.selected?.map((item) => (
                <div className="content-sync-review-row" key={`selected-${item.contentType}-${item.contentId}`}>
                  <span>{typeLabel(item.contentType)}</span>
                  <strong>{item.title}</strong>
                </div>
              ))}
            </div>

            {!!sync.reviewPlan.required?.length && (
              <div className="content-sync-review-section required">
                <h3>Required dependencies</h3>
                {sync.reviewPlan.required.map((item) => (
                  <div className="content-sync-review-row" key={`required-${item.contentType}-${item.contentId}`}>
                    <span>{typeLabel(item.contentType)}</span>
                    <strong>{item.title}</strong>
                    <small>{item.reason}</small>
                  </div>
                ))}
              </div>
            )}

            {!!sync.reviewPlan.warnings?.length && (
              <div className="content-sync-warning-box">
                {sync.reviewPlan.warnings.map((warning) => <p key={warning}>{warning}</p>)}
              </div>
            )}

            <div className="content-sync-summary">
              <span>{sync.reviewPlan.summary?.chapters || 0} chapters</span>
              <span>{sync.reviewPlan.summary?.frames || 0} frames</span>
              <span>{sync.reviewPlan.summary?.graphs || 0} graphs</span>
              <span>{sync.reviewPlan.summary?.frameBindings || 0} bindings</span>
            </div>

            <div className="content-sync-modal-actions">
              <button className="secondary-button" type="button" onClick={() => sync.setReviewPlan(null)}>
                Cancel
              </button>
              <button className="primary-button" type="button" disabled={sync.applyLoading} onClick={sync.apply}>
                {sync.applyLoading ? "Syncing..." : "Sync to Production"}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
